<script lang="ts" setup>
import { computed, watch } from "vue";
import { getLocationLabels, getDocumentIds } from "@/services/api/locationlabel.api";
import GridPagination from "@/components/general/GridPagination.vue";
import useDateTimeFormatter from "@/composables/formatDateTime.js";
import useGrid27 from "@/composables/grid27.js";
import { useCacheStore } from "@/stores/cache";
import { useAppConfigurationStore } from "@/stores/appconfig";
import { useToast } from "vue-toastification";
import { EnergyLabelCategoryStatus } from "@/enums/EnergyLabelCategoryStatus";
import { formatThousand } from "@/composables/formatNumbers";
const toaster = useToast();

const { formatDateUtc } = useDateTimeFormatter();
const appconfigStore = useAppConfigurationStore();
const { fetchFirstPage, gridData, gridIsLoading, pageNumber, pageSize, pageCount, totalItems, changedSortBy, changedPaging } = useGrid27({
  getData: getData,
  fetchDataError: fetchError,
});

const props = defineProps({
  partyReference: { type: String, required: true },
  filterData: { type: Array, default: () => [] },
});

const emits = defineEmits(["fetch-data-complete"]);

const _headers = [
  { title: "Volledigheid", key: "dataScore", align: "center" },
  { title: "VHE nummer", key: "reference" },
  { title: "Complexnummer", key: "parentReference" },
  { title: "Adres", key: "address", cellProps: { colSpan: "2" } },
  { title: "Plaats", key: "city", cellProps: { class: "d-none" } },

  {
    title: "Huidig label (Vabi)",
    align: "start",
    children: [
      { title: "Label", key: "vabiCurrentLabel", cellProps: { class: "color-light1" } },
      { title: "EP2 kWh/m²", key: "vabiCurrentLabelBeng2", align: "end", cellProps: { class: "color-light1" } },
    ],
    headerProps: { class: "no-rotate color-light1" },
  },
  {
    title: "Geregistreerd label (VABI)",
    align: "start",
    children: [
      { title: "Label", key: "vabiRegisteredLabel", cellProps: { class: "color-light2" } },
      { title: "EI", key: "vabiRegisteredEnergyIndex", align: "end", cellProps: { class: "color-light2" } },
      { title: "EP2 kWh/m²", key: "vabiRegisteredLabelBeng2", align: "end", cellProps: { class: "color-light2" } },
      { title: "Opnamedatum", key: "vabiRegisteredLabelRecordedDate", cellProps: { class: "color-light2" } },
      { title: "Registratiedatum", key: "vabiRegistrationDate", cellProps: { class: "color-light2" } },
      { title: "Registratienummer", key: "vabiRegistrationNumber", cellProps: { class: "color-light2" } },
    ],
    headerProps: { class: "no-rotate color-light2" },
  },
  {
    title: "Geregistreerd label (EPOnline)",
    align: "start",
    children: [
      { title: "Label", key: "epOnlineLabel", align: "end", cellProps: { class: "color-light3" } },
      { title: "EI", key: "epOnlineEnergyIndex", align: "end", cellProps: { class: "color-light3" } },
      { title: "EP2 kWh/m²", key: "epOnlineLabelBeng2", cellProps: { class: "color-light3" } },
      { title: "Opnamedatum", key: "epOnlineRecordedDate", cellProps: { class: "color-light3" } },
      { title: "Registratiedatum", key: "epOnlineRegistrationDate", cellProps: { class: "color-light3" } },
    ],
    headerProps: { class: "no-rotate color-light3" },
  },
  { title: "Document", key: "documentAvailable", sortable: false, align: "center", headerProps: { class: "no-rotate" } },
];
const headers = computed(() => _headers);

watch(
  () => [props.partyReference, props.filterData],
  () => {
    fetchFirstPage();
  },
  { immediate: true, deep: true }
);

async function getData(pageNumber : number, pageSize : number, sortBy : string, sortDesc : string) {
  if (props.filterData) {
    const pageNumber0 = pageNumber - 1;
    const response = await getLocationLabels(props.partyReference, pageNumber0, pageSize, sortBy, sortDesc, props.filterData);
    console.log("%cLocationLabelGrid, getData, response: ", "background: purple; color: white", response);
    emits("fetch-data-complete", true);
    return response;
  } else {
    return undefined;
  }
}

function fetchError(value) {
  console.log("LocationLabelGrid fetchError", value);
  toaster.error("Ophalen van de data is mislukt! " + value);
}

async function downloadDocument(item) {
  // De pandenlijst weet nu alleen als er een document is binnen de document service. Het document zelf is niet bekend in de pandenlijst.
  // console.log("%cLocationLabelGrid, downloadDocument", "background: purple; color: white", item.locationReference);
  const cacheStore = useCacheStore();
  console.log("LocationLabelGrid downloadDocument", item);

  if (cacheStore.isDocumentCached(item.locationReference)) {
    // console.log("LocationLabelGrid, downloadDocument cached ");
    const cachedDocument = cacheStore.getDocumentInfo(item.locationReference);
    item.documentId = cachedDocument.documentId;
    item.documentDescription = cachedDocument.description;
  } else {
    // console.log("LocationLabelGrid, downloadDocument niet cached ");
    const response = await getDocumentIds("EnergyLabel", item.locationReference, item.vabiRegistrationNumber);
    console.log("%cLocationLabelGrid, response: ", "background: purple; color: white", item.locationReference, item.vabiRegistrationNumber, response);
    if (response && response.length > 0) {
      item.documentId = response[0].id;
      item.documentDescription = response[0].description;
      cacheStore.cacheDocumentInfo(item.locationReference, item.documentId, item.documentDescription);
    } else {
      item.documentId = undefined;
      item.documentDescription = undefined;
    }
  }

  if (item.documentId) {
    let link = document.createElement("a");
    link.href = appconfigStore.portalUrl + "api/asset/document/" + item.documentId;
    link.download = item.documentDescription;
    link.click();
  } else {
    toaster.error("Document niet beschikbaar!");
  }
}
</script>

<template>
  <div>
    <!-- items-length: noodzakelijk anders werkt sorting niet goed, gaat ie eerst client sorting doen en dan alsnog van server halen -->
    <!-- options.sync: dit werkt niet goed met externe paging, wordt dus alleen gebruikt voor sorting -->
    <v-data-table-server
      :headers="headers"
      hide-default-footer
      :items="gridData"
      :loading="gridIsLoading"
      :items-per-page="pageSize"
      :items-length="totalItems"
      class="overview-table rotate-header"
      @update:sortBy="changedSortBy"
    >
      <template v-slot:[`item.dataScore`]="{ item }">
        <v-icon v-if="item.dataScore == EnergyLabelCategoryStatus.Error" large color="red"> mdi-alert-octagon </v-icon>
        <v-icon v-else-if="item.dataScore == EnergyLabelCategoryStatus.Warning" large color="orange"> mdi-alert </v-icon>
        <v-icon v-else-if="item.dataScore == EnergyLabelCategoryStatus.Ok" large color="green"> mdi-checkbox-marked-circle </v-icon>
        <v-icon v-else>mdi-tilde</v-icon>
      </template>

      <template v-slot:[`item.address`]="{ item }">
        <div>{{ item.address }}</div>
        <div>{{ item.postalCode }} {{ item.city }}</div>
      </template>
      <template v-slot:[`item.reference`]="{ item }">
        <v-tooltip location="bottom">
          <template v-slot:activator="{ props }">
            <div v-bind="props" class="d-flex align-center justify-space-between">
              {{ item.reference }}
            </div>
          </template>
          <span> {{ item.description }} (locRef: {{ item.locationReference }}) </span>
        </v-tooltip>
      </template>

      <template v-slot:[`item.vabiCurrentLabelBeng2`]="{ item }">
        <span v-if="item.vabiCurrentLabelBeng2 != null">{{ formatThousand(2, item.vabiCurrentLabelBeng2) }}</span>
      </template>
      <template v-slot:[`item.vabiCurrentLabel`]="{ item }">
        <energy-label :label="item.vabiCurrentLabel" class="small"></energy-label>
      </template>

      <template v-slot:[`item.vabiRegisteredEnergyIndex`]="{ item }">
        <span v-if="item.vabiRegisteredEnergyIndex != null">{{ formatThousand(2, item.vabiRegisteredEnergyIndex) }}</span>
      </template>
      <template v-slot:[`item.vabiRegisteredLabelBeng2`]="{ item }">
        <span v-if="item.vabiRegisteredLabelBeng2 != null">{{ formatThousand(2, item.vabiRegisteredLabelBeng2) }}</span>
      </template>
      <template v-slot:[`item.vabiRegisteredLabel`]="{ item }">
        <energy-label :label="item.vabiRegisteredLabel" class="small"></energy-label>
      </template>
      <template v-slot:[`item.vabiRegisteredLabelRecordedDate`]="{ item }">
        {{ formatDateUtc(item.vabiRegisteredLabelRecordedDate) }}
      </template>
      <template v-slot:[`item.vabiRegistrationDate`]="{ item }">
        {{ formatDateUtc(item.vabiRegistrationDate) }}
      </template>

      <template v-slot:[`item.epOnlineEnergyIndex`]="{ item }">
        <span v-if="item.epOnlineEnergyIndex != null">{{ formatThousand(2, item.epOnlineEnergyIndex) }}</span>
      </template>
      <template v-slot:[`item.epOnlineLabelBeng2`]="{ item }">
        <span v-if="item.epOnlineLabelBeng2 != null">{{ formatThousand(2, item.epOnlineLabelBeng2) }}</span>
      </template>
      <template v-slot:[`item.epOnlineLabel`]="{ item }">
        <energy-label :label="item.epOnlineLabel" class="small"></energy-label>
      </template>
      <template v-slot:[`item.epOnlineRecordedDate`]="{ item }">
        {{ formatDateUtc(item.epOnlineRecordedDate) }}
      </template>
      <template v-slot:[`item.epOnlineRegistrationDate`]="{ item }">
        {{ formatDateUtc(item.epOnlineRegistrationDate) }}
      </template>

      <template v-slot:[`item.documentAvailable`]="{ item }">
        <div v-if="item.documentAvailable == 'True'" v-bind="props">
          <v-icon @click="downloadDocument(item)" large color="primary"> mdi-file </v-icon>
        </div>
        <div v-else v-bind="props">
          <v-icon medium color="gray"> mdi-file-hidden </v-icon>
        </div>
      </template>
    </v-data-table-server>
    <grid-pagination :pageNumber="pageNumber" :pageSize="pageSize" :pageCount="pageCount" @changedPaging="changedPaging"></grid-pagination>
  </div>
</template>
